import { useState } from 'react';
import { Segmented } from '@/components/ui/kit';
import { useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy from '@/i18n/ns/storefront';
import { EmptyState, ErrorState } from '../primitives';
import { WorkspaceIcon } from '../icons';
import { useStorefrontApi, type StoreDesign, type StoreDetail } from './api';
import { useRemote } from './use-storefront';

/** Base64url of the design's UTF-8 JSON, for the frame's fragment (demo, or a proposal over the preview). */
export function encodeDesign(design: StoreDesign): string {
  const bytes = new TextEncoder().encode(JSON.stringify(design));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

/** Before the first version exists, the demo widget shows the design in the store's colour and logo. */
export function designPreviewUrl(store: StoreDetail, device: 'computer' | 'phone'): string | null {
  if (!store.assistantOrigin) return null;
  const query = new URLSearchParams({
    demo: '1',
    open: '1',
    name: store.design?.copy?.assistantName ?? store.name,
  });
  const brand = store.found?.look.colours[0];
  if (brand?.startsWith('#') && brand.length === 7) query.set('brand', brand.slice(1));
  if (store.found?.look.logo) query.set('logo', store.found.look.logo);
  const fragment = new URLSearchParams();
  if (Object.keys(store.design ?? {}).length) fragment.set('design', encodeDesign(store.design));
  if (device === 'phone') fragment.set('fill', 'screen');
  return `${store.assistantOrigin}/w/frame.html?${query}${fragment.size ? `#${fragment}` : ''}`;
}

/** The real widget, served by the assistant service in preview mode, on a quiet stage; before
 *  the store is read, the demo widget in the design so far. Changes are made with Chat. */
export function WidgetTab({ store, onDesign }: { store: StoreDetail; onDesign: () => void }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const ready = store.status === 'ready' || store.status === 'live';
  const api = useStorefrontApi();
  // On a narrow screen the preview starts as a phone shows it: the desktop panel would not fit.
  const [device, setDevice] = useState<'computer' | 'phone'>(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches
      ? 'phone'
      : 'computer',
  );
  const preview = useRemote(
    ready ? () => api.preview(store.id) : null,
    `${store.id}:${store.draft?.version ?? store.live?.version ?? 0}`,
  );
  const early = ready ? null : designPreviewUrl(store, device);
  const frameSrc = ready
    ? preview.data
      ? `${preview.data.frameUrl}${device === 'phone' ? '&fill=screen' : ''}`
      : null
    : early;
  if (!ready && !early)
    return <EmptyState icon="store" title={copy.widgetTitle} description={copy.widgetNotReady} />;
  return (
    <div className="sf-widget">
      {/* One row above the stage, as in the canvas: what it is on the left, the screen to show
          it on and the one way to change it (Chat) on the right. */}
      <header className="sf-widget-head">
        <div className="sf-widget-copy">
          <h2>{copy.widgetTitle}</h2>
          <p>{ready ? copy.widgetBody : copy.designPreviewNote}</p>
        </div>
        <div className="sf-widget-actions">
          <Segmented
            label={copy.previewSize}
            options={[
              { value: 'computer', label: copy.deviceComputer },
              { value: 'phone', label: copy.devicePhone },
            ]}
            value={device}
            onChange={setDevice}
          />
          <button className="ws-button primary" data-testid="sf-design-chat" onClick={onDesign}>
            <WorkspaceIcon name="chat" size={14} />
            {copy.designWithChat}
          </button>
        </div>
      </header>
      <div className="sf-stage">
        <span className="sf-stage-site" aria-hidden="true">
          {store.domain}
        </span>
        {ready && preview.error ? (
          <ErrorState message={copy.widgetFailed} onRetry={preview.refresh} />
        ) : frameSrc ? (
          <iframe
            className="sf-stage-frame"
            data-testid="sf-stage-frame"
            data-device={device}
            title={copy.assistantTitle}
            src={frameSrc}
            allow="clipboard-write"
            // Product links open in a new tab; the preview can never navigate Chat itself.
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
        ) : (
          <p className="sf-stage-loading" role="status">
            {copy.widgetLoading}
          </p>
        )}
      </div>
    </div>
  );
}
